import { useCallback, useEffect, useRef } from "react"

// Older browsers without BroadcastChannel degrade to per-tab behavior:
// the no-op stub keeps call sites unconditional and silently drops messages.
const BroadcastChannelImpl = typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? window.BroadcastChannel
    : class { postMessage() {} close() {} }

// Cross-tab auth coordination. Handlers fire for inbound messages from
// sibling tabs; the returned `broadcast(type, payload?)` publishes outbound
// messages. Handlers may change identity between renders without re-binding
// the channel — routed through a ref so the latest closure always wins.
export const useAuthBroadcast = ({ workspaceName, onRemoteLogin, onRemoteRefresh, onRemoteLogout }) => {
    const handlersRef = useRef()
    handlersRef.current = { onRemoteLogin, onRemoteRefresh, onRemoteLogout }

    const bcRef = useRef(null)

    useEffect(() => {
        const bc = new BroadcastChannelImpl(`iam-auth-${workspaceName}`)
        bcRef.current = bc

        bc.onmessage = (event) => {
            const handlers = handlersRef.current
            switch (event.data?.type) {
                case 'logged-in':
                    handlers.onRemoteLogin?.()
                    break
                case 'session-refreshed':
                    handlers.onRemoteRefresh?.(event.data)
                    break
                case 'logged-out':
                    handlers.onRemoteLogout?.()
                    break
                default:
                    break
            }
        }

        return () => {
            bcRef.current = null
            bc.close()
        }
    }, [workspaceName])

    const broadcast = useCallback((type, payload) => {
        bcRef.current?.postMessage(payload ? { type, ...payload } : { type })
    }, [])

    return { broadcast }
}
